<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { string } from 'yup';
import GenericModal from '@shared/ui/components/modals/GenericModal.vue';
import ButtonComponent from '@shared/ui/components/ButtonComponent.vue';
import GenericSelector from '@shared/ui/components/GenericSelector.vue';
import { socialService } from '@features/social/api/social.service';
import { useSocialStore } from '@/stores/social';
import type {
  SocialUser,
  Gender,
} from '@features/social/api/social.service.types';
import { toast } from '@shared/utils/toast';
// City data attribution (required by the dataset license):
//   Source: GeoNames Gazetteer — https://www.geonames.org
//   Packaged by the `cities.json` npm package (https://github.com/lutangar/cities.json)
//   Licensed under CC-BY-4.0 (https://creativecommons.org/licenses/by/4.0/).
// The dataset is ~17 MB, so it is imported as a hashed static asset (URL) and
// fetched at runtime instead of as a module — otherwise Rollup parses the whole
// JSON into the bundle graph and OOMs the production build.
import citiesUrl from 'cities.json?url';

interface CityRecord {
  name: string;
  country: string; // ISO 3166-1 alpha-2
}

interface Props {
  modelValue: boolean;
}

interface ProfileFormValues {
  firstName: string;
  lastName: string;
  gender: Gender | '';
  birthDate: string;
  country: string;
  city: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  save: [payload: ProfileFormValues];
}>();

const socialStore = useSocialStore();

const show = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const initialValues: ProfileFormValues = {
  firstName: '',
  lastName: '',
  gender: '',
  birthDate: '',
  country: '',
  city: '',
};

const getMinBirthDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  return date;
};

const validationSchema = {
  firstName: string().required('Ingresa tu nombre.'),
  lastName: string().required('Ingresa tu apellido.'),
  gender: string().required('Selecciona tu género.'),
  birthDate: string()
    .required('Ingresa tu fecha de nacimiento.')
    .test('is-adult', 'Debes ser mayor o igual de 18 años.', (value) => {
      if (!value) return true;
      const birthDate = new Date(value);
      const today = new Date();

      const birthYear = birthDate.getFullYear();
      const birthMonth = birthDate.getMonth();
      const birthDay = birthDate.getDate();

      const todayYear = today.getFullYear();
      const todayMonth = today.getMonth();
      const todayDay = today.getDate();

      let age = todayYear - birthYear;

      if (
        todayMonth < birthMonth ||
        (todayMonth === birthMonth && todayDay < birthDay)
      ) {
        age--;
      }

      return age >= 18;
    }),
  country: string().required('Ingresa tu país.'),
  city: string().required('Ingresa tu ciudad.'),
};

const { handleSubmit, errors, setValues, isSubmitting } =
  useForm<ProfileFormValues>({
    initialValues,
    validationSchema,
  });

const { value: firstName } = useField<string>('firstName');
const { value: lastName } = useField<string>('lastName');
const { value: gender } = useField<string>('gender');
const { value: birthDate } = useField<string>('birthDate');
const { value: city } = useField<string>('city');

const genderOptions = [
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
];

// Country is auto-detected from the backend (legend-geo) and locked for editing.
const detectedCountryCode = ref('');
const detectedCountryName = ref('');
const isCountryEnabled = ref(false);
const geoLoading = ref(false);
const geoFailed = ref(false);

const loadDetectedCountry = async () => {
  geoLoading.value = true;
  geoFailed.value = false;
  try {
    const { data } = await socialService.getDetectedCountry();
    if (data.country) {
      detectedCountryCode.value = data.country;
      detectedCountryName.value = data.countryName || data.country;
      isCountryEnabled.value = data.isEnabled;
      setValues({ country: data.country });
    } else {
      detectedCountryCode.value = '';
      detectedCountryName.value = '';
      isCountryEnabled.value = false;
      geoFailed.value = true;
      setValues({ country: '' });
    }
  } catch (error) {
    console.error(error);
    detectedCountryCode.value = '';
    detectedCountryName.value = '';
    isCountryEnabled.value = false;
    geoFailed.value = true;
    setValues({ country: '' });
  } finally {
    geoLoading.value = false;
  }
};

// Save is blocked until the country is detected (no manual selection allowed).
const canSubmit = computed(
  () => Boolean(detectedCountryCode.value) && !isSubmitting.value,
);

// --- City dropdown ---------------------------------------------------------
// The city becomes a searchable select populated with the cities of the
// geo-detected country. Data comes from `cities.json` (GeoNames, CC-BY-4.0),
// lazy-imported only when this modal needs it so the ~MB dataset stays out of
// the main bundle.
const cityQuery = ref('');
const cityOptions = ref<string[]>([]);
const cityLoading = ref(false);
const cityLoadFailed = ref(false);
const cityDropdownOpen = ref(false);

const loadCitiesForCountry = async (code: string) => {
  cityOptions.value = [];
  cityLoadFailed.value = false;
  if (!code) return;
  cityLoading.value = true;
  try {
    const response = await fetch(citiesUrl);
    if (!response.ok)
      throw new Error(`cities fetch failed: ${response.status}`);
    const allCities = (await response.json()) as CityRecord[];
    const upper = code.toUpperCase();
    const seen = new Set<string>();
    const names: string[] = [];
    for (const c of allCities) {
      if (c.country === upper && c.name && !seen.has(c.name)) {
        seen.add(c.name);
        names.push(c.name);
      }
    }
    names.sort((a, b) => a.localeCompare(b));
    cityOptions.value = names;
  } catch (error) {
    console.error('Failed to load cities dataset', error);
    cityLoadFailed.value = true;
  } finally {
    cityLoading.value = false;
  }
};

// Cap the rendered list; the full country set can be thousands of cities.
const filteredCities = computed(() => {
  const q = cityQuery.value.trim().toLowerCase();
  const source = q
    ? cityOptions.value.filter((name) => name.toLowerCase().includes(q))
    : cityOptions.value;
  return source.slice(0, 50);
});

const selectCity = (name: string) => {
  city.value = name;
  cityQuery.value = name;
  cityDropdownOpen.value = false;
};

const onCityInput = () => {
  cityDropdownOpen.value = true;
  if (cityLoadFailed.value) {
    // Dataset unavailable: accept free text so a chunk/CDN failure never blocks
    // registration.
    city.value = cityQuery.value.trim();
  } else {
    // Require an explicit pick from the list; invalidate any stale selection.
    city.value = '';
  }
};

const closeCityDropdown = () => {
  // Defer so a click on an option is registered before the list unmounts.
  setTimeout(() => {
    cityDropdownOpen.value = false;
  }, 150);
};

// Reload cities for the detected country, preserving a pre-filled city only when
// it is a valid city of that country.
watch(detectedCountryCode, async (code) => {
  await loadCitiesForCountry(code);
  const current = (city.value || '').trim();
  if (cityLoadFailed.value) {
    cityQuery.value = current;
  } else if (current && cityOptions.value.includes(current)) {
    cityQuery.value = current;
  } else {
    city.value = '';
    cityQuery.value = '';
  }
});

const formatBirthdayForInput = (
  birthday: SocialUser['birthday'] | undefined,
) => {
  if (!birthday) return '';

  if (typeof birthday === 'string') {
    return birthday.split('T')[0];
  }

  if (birthday instanceof Date && !Number.isNaN(birthday.getTime())) {
    const year = birthday.getFullYear();
    const month = String(birthday.getMonth() + 1).padStart(2, '0');
    const day = String(birthday.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return '';
};

watch(
  () => show.value,
  (val) => {
    if (val && socialStore._socialUser) {
      const u = socialStore._socialUser;
      const uCity = u.city || u.location?.city || '';

      setValues({
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        gender: (u.gender === 'UNDEFINED' ? '' : u.gender) || '',
        birthDate: formatBirthdayForInput(u.birthday),
        city: uCity === 'Unknown' ? '' : uCity,
      });

      // Country is resolved from geolocation, not from the stored profile.
      void loadDetectedCountry();
    }
  },
);

const handleSave = handleSubmit(async (values) => {
  if (!detectedCountryCode.value) {
    toast.error(
      'No pudimos detectar tu país. Recargá la página e intentá de nuevo.',
    );
    return;
  }

  try {
    const payload: Partial<SocialUser> = {
      firstName: values.firstName,
      lastName: values.lastName,
      gender: values.gender as Gender,
      birthday: values.birthDate,
      // ISO 3166-1 alpha-2 code from geolocation (consistent with the Country Registry).
      country: detectedCountryCode.value,
      city: values.city,
    };

    await socialService.updateSocialUser(payload);
    await socialStore.refreshMyUser();
    emit('save', values);
    show.value = false;
    toast.success({ title: 'Perfil actualizado correctamente' });
  } catch (error) {
    console.error(error);
    toast.error('Error al actualizar el perfil');
  }
});
</script>

<template>
  <GenericModal
    v-model="show"
    size="2xl"
    title="Completa tu perfil"
    container-class="bg-background border border-muted"
    :hide-close-button="false"
    :close-to-click-outside="false"
    :mobile-fullscreen="true"
    icon="ph:user"
    :is-icon="true"
    content-class="max-h-[70vh] overflow-y-auto"
  >
    <div class="space-y-4 text-white">
      <p class="text-sm text-muted-foreground">
        Necesitamos algunos datos para personalizar tu experiencia y generar
        informes de campañas.
      </p>

      <div class="grid gap-4 md:grid-cols-2">
        <div>
          <label class="mb-2 block text-sm font-semibold"
            >Nombre <span class="text-red-500">*</span></label
          >
          <input
            v-model="firstName"
            type="text"
            placeholder="Tu nombre"
            class="w-full rounded-xl border border-muted bg-background px-4 py-3 text-base text-white outline-none transition-all focus:border-primary"
          />
          <span v-if="errors.firstName" class="ml-1 text-xs text-red-500">{{
            errors.firstName
          }}</span>
        </div>
        <div>
          <label class="mb-2 block text-sm font-semibold"
            >Apellido <span class="text-red-500">*</span></label
          >
          <input
            v-model="lastName"
            type="text"
            placeholder="Tu apellido"
            class="w-full rounded-xl border border-muted bg-background px-4 py-3 text-base text-white outline-none transition-all focus:border-primary"
          />
          <span v-if="errors.lastName" class="ml-1 text-xs text-red-500">{{
            errors.lastName
          }}</span>
        </div>
      </div>

      <div>
        <label class="mb-2 block text-sm font-semibold"
          >Género <span class="text-red-500">*</span></label
        >
        <GenericSelector
          :model-value="gender"
          :options="genderOptions"
          placeholder="Selecciona tu género"
          container-class="bg-background border-muted"
          :error="!!errors.gender"
          :error-message="errors.gender"
          @update:model-value="setValues({ gender: $event as Gender })"
        />
      </div>

      <div>
        <label class="mb-2 flex items-center gap-2 text-sm font-semibold">
          <IconifyIcon icon="ph:calendar-blank" class="size-4 text-white/60" />
          Fecha de nacimiento <span class="text-red-500">*</span>
        </label>
        <input
          v-model="birthDate"
          type="date"
          :max="getMinBirthDate().toISOString().split('T')[0]"
          class="w-full rounded-xl border border-muted bg-background px-4 py-3 text-base text-white outline-none transition-all focus:border-primary"
        />
        <span v-if="errors.birthDate" class="ml-1 text-xs text-red-500">{{
          errors.birthDate
        }}</span>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <div>
          <label class="mb-2 flex items-center gap-2 text-sm font-semibold">
            <IconifyIcon icon="ph:map-pin" class="size-4 text-white/60" />
            País <span class="text-red-500">*</span>
          </label>
          <div
            class="flex w-full items-center justify-between rounded-xl border border-muted bg-background/60 px-4 py-3 text-base"
            :class="geoFailed ? 'border-red-500/60' : ''"
          >
            <span
              :class="
                detectedCountryName ? 'text-white' : 'text-muted-foreground'
              "
            >
              {{
                geoLoading
                  ? 'Detectando tu ubicación…'
                  : detectedCountryName || 'No detectado'
              }}
            </span>
            <IconifyIcon icon="ph:lock-simple" class="size-4 text-white/40" />
          </div>
          <span class="ml-1 mt-1 block text-xs text-muted-foreground">
            Detectado automáticamente según tu ubicación.
          </span>
          <span v-if="geoFailed" class="ml-1 block text-xs text-red-500">
            No pudimos detectar tu país. Recargá la página e intentá de nuevo.
          </span>
        </div>
        <div>
          <label class="mb-2 block text-sm font-semibold"
            >Ciudad <span class="text-red-500">*</span></label
          >
          <div class="relative">
            <input
              v-model="cityQuery"
              type="text"
              autocomplete="off"
              :disabled="!detectedCountryCode || cityLoading"
              :placeholder="
                cityLoading
                  ? 'Cargando ciudades…'
                  : detectedCountryCode
                    ? 'Buscá tu ciudad'
                    : 'Detectando país…'
              "
              class="w-full rounded-xl border border-muted bg-background px-4 py-3 text-base text-white outline-none transition-all focus:border-primary disabled:cursor-not-allowed disabled:opacity-60"
              @focus="cityDropdownOpen = true"
              @input="onCityInput"
              @blur="closeCityDropdown"
            />
            <ul
              v-if="cityDropdownOpen && filteredCities.length"
              class="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-muted bg-background shadow-lg"
            >
              <li
                v-for="name in filteredCities"
                :key="name"
                class="cursor-pointer px-4 py-2 text-sm text-white transition-colors hover:bg-primary/20"
                @mousedown.prevent="selectCity(name)"
              >
                {{ name }}
              </li>
            </ul>
          </div>
          <span
            v-if="cityLoadFailed"
            class="ml-1 mt-1 block text-xs text-muted-foreground"
          >
            No pudimos cargar el listado de ciudades; escribí tu ciudad
            manualmente.
          </span>
          <span v-if="errors.city" class="ml-1 text-xs text-red-500">{{
            errors.city
          }}</span>
        </div>
      </div>

      <div
        class="rounded-xl border border-muted bg-background p-4 text-sm text-muted-foreground"
      >
        <span class="text-primary">*</span> Campos obligatorios. Esta
        información nos ayuda a generar informes para marcas y mejorar tu
        experiencia en la plataforma.
      </div>
    </div>

    <template #footer>
      <div class="mt-2 flex justify-end gap-3">
        <ButtonComponent
          variant="primary"
          width="sm"
          :is-loading="isSubmitting"
          :disabled="!canSubmit"
          @click="handleSave"
        >
          Guardar y continuar
        </ButtonComponent>
      </div>
    </template>
  </GenericModal>
</template>
